import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdlRecord } from "../../../store/slices/adlRecordsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${d.getFullYear()}`;
};

export default function ADLRecordPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentAdlRecord, isLoading, error } = useSelector((state) => state.adlRecords);

  useEffect(() => {
    if (id) dispatch(fetchAdlRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentAdlRecord?.record) {
      const timer = setTimeout(() => window.print(), 400);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentAdlRecord]);

  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentAdlRecord?.record) return <div className="p-4 text-center">No record found.</div>;

  const rec = currentAdlRecord.record;
  const activityRecords = rec.activity_records || [];

  // Step 1: Get unique dates (sorted)
  const uniqueDates = [...new Set(activityRecords.map((r) => formatDate(r.date)))].sort(
    (a, b) => new Date(a.split("/").reverse().join("-")) - new Date(b.split("/").reverse().join("-"))
  );

  // Group unique dates into chunks of 14 days (2 weeks)
  const chunkSize = 14;
  const dateChunks = [];
  for (let i = 0; i < uniqueDates.length; i += chunkSize) {
    dateChunks.push(uniqueDates.slice(i, i + chunkSize));
  }
  if (dateChunks.length === 0) {
    dateChunks.push([]);
  }

  // Step 3: Group by activity_id
  const activityMap = {};
  activityRecords.forEach((r) => {
    const actId = r.activity_id;
    if (!activityMap[actId]) {
      activityMap[actId] = {
        name: r.activity?.name || "Activity",
        sort_order: r.activity?.sort_order || 0,
        scores: {},
      };
    }
    const dateKey = formatDate(r.date);
    activityMap[actId].scores[dateKey] = {
      score: r.score,
      totalScore: r.total_score,
      staffName: r.staff_name,
    };
  });

  // Step 4: Sort activities
  const activities = Object.values(activityMap).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <PrintWrapper>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
        }

        body {
          font-family: Arial, sans-serif !important;
          line-height: 1.4 !important;
          color: #000000 !important;
          background-color: #ffffff !important;
          font-size: 9pt !important;
        }

        .print-container {
          display: block !important;
          width: 100% !important;
          padding: 20px !important;
          margin: 0 auto !important;
          position: relative !important;
        }

        .logo-container {
          display: flex !important;
          justify-content: flex-start !important;
          margin-bottom: 10px !important;
        }

        .logo-container img {
          height: 50px !important;
          width: auto !important;
        }

        .header h3 {
          font-size: 1.4em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 10px !important;
          margin-bottom: 8px !important;
          color: #000000 !important;
        }

        .header h6 {
          font-size: 1.1em !important;
          text-align: center !important;
          margin-bottom: 15px !important;
          font-weight: 600 !important;
          color: #000000 !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 14px !important;
          font-size: 9.5pt !important;
        }

        .underline {
          display: inline-block !important;
          width: auto !important;
          border-bottom: 1px solid #000 !important;
          margin-left: 5px !important;
          padding-right: 10px;
        }

        .instruction {
          margin: 12px 0 !important;
          font-size: 9pt !important;
          border: 1px solid #ddd !important;
          padding: 8px !important;
          background-color: #f9f9f9 !important;
        }

        .instruction p {
          font-weight: bold !important;
          margin-bottom: 4px !important;
        }

        .instruction ul {
          list-style: none !important;
          padding-left: 0 !important;
          margin: 0 !important;
          display: grid !important;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 5px;
        }

        .instruction li {
          margin-bottom: 2px !important;
        }

        .main-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 15px !important;
          font-size: 8.5pt !important;
        }

        .main-table th,
        .main-table td {
          border: 0.75pt solid #000 !important;
          padding: 3px 2px !important;
          text-align: center !important;
          vertical-align: middle !important;
        }

        .main-table th {
          background-color: #d0cece !important;
          font-weight: bold !important;
        }

        .activity-name {
          text-align: left !important;
          font-weight: bold !important;
          padding-left: 6px !important;
        }

        .checkbox {
          display: inline-block !important;
          width: 12px !important;
          height: 12px !important;
          border: 1px solid #000 !important;
          margin-left: 3px !important;
          line-height: 10px !important;
          font-size: 10px !important;
          font-weight: bold !important;
          text-align: center !important;
        }

        .checked::before {
          content: "✓" !important;
        }

        ${printFooterStyles}

        @media print {
          .print-container {
            padding: 10px !important;
          }
          .page-break {
            page-break-after: always !important;
            clear: both !important;
          }
        }
      `}</style>

      {dateChunks.map((dateColumns, chunkIdx) => {
        const totalDateCols = dateColumns.length;
        const colWidth = totalDateCols > 0 ? `${60 / totalDateCols}%` : "5%";

        const activityStats = activities.map((act) => {
          let totalScore = 0;
          const staffSet = new Set();

          dateColumns.forEach((date) => {
            const scoreData = act.scores[date] || {};
            if (scoreData.score) totalScore += scoreData.score;
            if (scoreData.staffName) staffSet.add(scoreData.staffName.trim());
          });

          return {
            ...act,
            totalScore,
            staffNames: Array.from(staffSet).join(", ") || "",
          };
        });

        const isLastChunk = chunkIdx === dateChunks.length - 1;
        const chunkDateRange = dateColumns.length > 0
          ? `${dateColumns[0]} till ${dateColumns[dateColumns.length - 1]}`
          : "No records";

        return (
          <div
            key={chunkIdx}
            className={`print-container ${!isLastChunk ? "page-break" : ""}`}
          >
            {/* Logo */}
            <div className="logo-container">
              <img src={logo} alt="The Recovery House" />
            </div>

            {/* Header */}
            <div className="header">
              <h3>THE RECOVERY HOUSE</h3>
              <h6>ADL RECORD SHEET</h6>
            </div>

            {/* Client Info */}
            <div className="info-row">
              <div>
                <span>Client Name:</span>
                <span className="underline fw-bold">{rec.client_name || " "}</span>
              </div>
              <div>
                <span>Room No.:</span>
                <span className="underline fw-bold">{rec.room_number || " "}</span>
              </div>
              <div>
                <span>Date Range:</span>
                <span className="underline fw-bold">{chunkDateRange}</span>
              </div>
            </div>

            {/* Instruction / Scoring System */}
            <div className="instruction">
              <p><strong>Instruction: Scoring System</strong></p>
              <ul>
                <li><strong>1 = Independent</strong> (Client performs the activity independently)</li>
                <li><strong>2 = Supervision</strong> (Client performs the activity with supervision)</li>
                <li><strong>3 = Totally Dependent</strong> (Client requires assistance to perform the activity)</li>
              </ul>
            </div>

            {/* Dynamic Table */}
            <table className="main-table">
              <thead>
                <tr>
                  <th style={{ width: "16%" }}>Activity</th>
                  {dateColumns.map((date, i) => (
                    <th key={i} style={{ width: colWidth, fontSize: "8pt" }}>
                      {date}
                    </th>
                  ))}
                  <th style={{ width: "10%" }}>Total Score (Out of 135)</th>
                  <th style={{ width: "14%" }}>Staff Name</th>
                </tr>
              </thead>

              <tbody>
                {activityStats.map((act, index) => (
                  <tr key={index}>
                    <td className="activity-name" style={{ width: "16%" }}>
                      {act.name}
                    </td>

                    {dateColumns.map((date, colIdx) => {
                      const scoreData = act.scores[date] || {};
                      return (
                        <td key={`${index}-${colIdx}`} style={{ width: colWidth, padding: "2px 0" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", width: "100%" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                              <span>1</span>
                              <span className={`checkbox ${scoreData.score === 1 ? "checked" : ""}`} />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                              <span>2</span>
                              <span className={`checkbox ${scoreData.score === 2 ? "checked" : ""}`} />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                              <span>3</span>
                              <span className={`checkbox ${scoreData.score === 3 ? "checked" : ""}`} />
                            </div>
                          </div>
                        </td>
                      );
                    })}

                    <td style={{ width: "10%", textAlign: "center" }}>
                      {act.totalScore}
                    </td>
                    <td style={{ width: "14%", textAlign: "center", fontSize: "8pt" }}>
                      {act.staffNames}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer */}
            <div className="footer">
              <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
              <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>

              <div className="footer-sign">
                <div>
                  <strong>Checked by:</strong> <span className="underline">{rec.checked_by || ""}</span>
                </div>
                <div>
                  <strong>Date:</strong> <span className="underline">{formatDate(rec.created_at) || " "}</span>
                </div>
              </div>

              <div className="footer-sign" style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                <div>
                  <strong>Nursing Manager / Head Nurse:</strong>{" "}
                  <span className="underline" style={{ minWidth: "180px", display: "inline-block" }}>
                    {rec.nursing_manager || " "}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </PrintWrapper>
  );
}